import { ColorPicker } from "antd";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  IRagRule,
  RagEndComparator,
  RagMeasurementType,
  RagSex,
  RagStartComparator,
} from "../../../../@types/Rag";
import { Close } from "../../../../assets/icons/Close";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { RuleError } from "../../validation";
import {
  colorPickerRootClassName,
  BoundaryRow,
  ColorPickerWrapper,
  DeleteButton,
  ErrorText,
  FieldContainer,
  FieldLabel,
  FieldLabelRow,
  FieldsRow,
  FieldsWrap,
  LabelInput,
  NumberInput,
  OverlapBanner,
  RequiredMark,
  RuleRowContainer,
} from "./RagRuleRow.styles";

// EN-5191: a factory, not a module constant. A module constant is evaluated at import time,
// before i18next has been initialised, so t() would return undefined and the dropdown would
// render blank labels. `value` stays the identity the form and the API use.
const buildSexOptions = (t: TFunction): IOption[] => [
  { label: t("rag_rule_sexMale"), value: "Male", key: "s-m" },
  { label: t("rag_rule_sexFemale"), value: "Female", key: "s-f" },
  { label: t("rag_rule_sexBoth"), value: "Both", key: "s-b" },
];

// Lower boundary: only ">" / "≥" are valid (backend rejects otherwise).
const START_COMPARATOR_OPTIONS: IOption[] = [
  { label: ">", value: "GreaterThan", key: "sc-gt" },
  { label: "≥", value: "GreaterThanOrEqualTo", key: "sc-gte" },
];

// Upper boundary: only "<" / "≤" are valid.
const END_COMPARATOR_OPTIONS: IOption[] = [
  { label: "<", value: "LessThan", key: "ec-lt" },
  { label: "≤", value: "LessThanOrEqualTo", key: "ec-lte" },
];

interface IRagRuleRowProps {
  rule: IRagRule;
  /**
   * EN-5123: the measurements available on the config's wards (union across them), labelled with the
   * staff member's own unit. Supplied by the page so every row shares one query; empty until a ward
   * is selected.
   */
  measurementOptions: IOption[];
  error: RuleError;
  canDelete: boolean;
  onChange: (updates: Partial<IRagRule>) => void;
  onDelete: () => void;
}

function Label({ text, required }: { text: string; required?: boolean }) {
  return (
    <FieldLabelRow>
      <FieldLabel>{text}</FieldLabel>
      {required && <RequiredMark>*</RequiredMark>}
    </FieldLabelRow>
  );
}

export function RagRuleRow({
  rule,
  measurementOptions,
  error,
  canDelete,
  onChange,
  onDelete,
}: IRagRuleRowProps) {
  const { t } = useTranslation();
  const sexOptions = useMemo(() => buildSexOptions(t), [t]);

  return (
    <RuleRowContainer>
      <FieldsWrap>
        <FieldsRow>
          <FieldContainer size="column">
            <Label text={t("rag_rule_column")} required />
            <SelectDropdown
              options={measurementOptions}
              value={rule.measurementType || undefined}
              placeholder={t("rag_rule_selectMeasurement")}
              height={42}
              width={189}
              disabled={measurementOptions.length === 0}
              optionFilterProp="label"
              onValueChange={(val) =>
                onChange({ measurementType: val as RagMeasurementType })
              }
            />
            {error.measurementType && (
              <ErrorText>{error.measurementType}</ErrorText>
            )}
          </FieldContainer>

          <FieldContainer size="boundary">
            <Label text={t("rag_rule_start")} required />
            <BoundaryRow>
              <SelectDropdown
                options={START_COMPARATOR_OPTIONS}
                value={rule.startComparator}
                placeholder=">"
                height={42}
                width={70}
                onValueChange={(val) =>
                  onChange({ startComparator: val as RagStartComparator })
                }
              />
              <NumberInput
                type="number"
                value={Number.isNaN(rule.startValue) ? "" : rule.startValue}
                hasError={!!error.startValue}
                onChange={(e) =>
                  onChange({ startValue: Number.parseFloat(e.target.value) })
                }
              />
            </BoundaryRow>
            {error.startValue && <ErrorText>{error.startValue}</ErrorText>}
          </FieldContainer>

          <FieldContainer size="boundary">
            <Label text={t("rag_rule_end")} required />
            <BoundaryRow>
              <SelectDropdown
                options={END_COMPARATOR_OPTIONS}
                value={rule.endComparator}
                placeholder="<"
                height={42}
                width={70}
                onValueChange={(val) =>
                  onChange({ endComparator: val as RagEndComparator })
                }
              />
              <NumberInput
                type="number"
                value={Number.isNaN(rule.endValue) ? "" : rule.endValue}
                hasError={!!error.endValue}
                onChange={(e) =>
                  onChange({ endValue: Number.parseFloat(e.target.value) })
                }
              />
            </BoundaryRow>
            {error.endValue && <ErrorText>{error.endValue}</ErrorText>}
          </FieldContainer>

          <FieldContainer size="color">
            <Label text={t("rag_rule_color")} required />
            <ColorPickerWrapper>
              <ColorPicker
                rootClassName={colorPickerRootClassName()}
                value={rule.color}
                disabledAlpha
                showText
                onChange={(value) => onChange({ color: value.toHexString() })}
              />
            </ColorPickerWrapper>
            {error.color && <ErrorText>{error.color}</ErrorText>}
          </FieldContainer>
        </FieldsRow>

        <FieldsRow>
          <FieldContainer size="sex">
            <Label text={t("rag_rule_sex")} />
            <SelectDropdown
              options={sexOptions}
              value={rule.sex}
              placeholder={t("rag_rule_select")}
              height={42}
              width={88}
              onValueChange={(val) => onChange({ sex: val as RagSex })}
            />
            {error.sex && <ErrorText>{error.sex}</ErrorText>}
          </FieldContainer>

          <FieldContainer size="label">
            <Label text={t("rag_rule_label")} />
            <LabelInput
              type="text"
              value={rule.label}
              maxLength={50}
              placeholder={t("rag_rule_label")}
              hasError={!!error.label}
              onChange={(e) => onChange({ label: e.target.value })}
            />
            {error.label && <ErrorText>{error.label}</ErrorText>}
          </FieldContainer>
        </FieldsRow>

        {error.overlap && <OverlapBanner>{error.overlap}</OverlapBanner>}
      </FieldsWrap>

      {canDelete && (
        <DeleteButton
          type="button"
          aria-label={t("rag_rule_delete")}
          onClick={onDelete}
        >
          <Close />
        </DeleteButton>
      )}
    </RuleRowContainer>
  );
}
